import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import Page from '@components/Page/Page';
import { copyToClipboard, usePlatformContext } from "@datassential/platform-ui-lib";
import KeywordSettings from "@components/KeywordSettings";
import TopTrendsMenuExamplePopup from "./TopTrendsMenuExamplePopup";
import useTopTrendsMenuExamples, { MainFilter } from "@apiHooks/useTopTrendsMenuExamples";
import TopTrendsTable from "./TopTrendsTable";
import TopTrendsControls from "./TopTrendsControls";
import FooterContent from "./FooterContent";
import { IconCopySimple } from "@icons";
import ActionIcon from "@components/ActionIcon";
import { addNotification } from "@actions/notifications.actions";
import TableInfo from "@components/TableInfo/TableInfo";
import { TopTrendsProvider, useTopTrends } from "./TopTrendsContext";

const TopTrendsContent: FC = () => {
    const dispatch = useDispatch();
    const [isShown, setIsShown] = useState(false);
    const {
        totalRecords,
        backendFilters,
        showOnlyNewItems,
        includeLowPenetrationItems,
        includeLessTenMenusCount,
        growthPeriod,
        drillWords,
        onDeleteDrillWord,
        keywordSettingsState,
        onCloseKeywordSettings,
        menuExamplesWordData,
        isMenuExamplesPopupOpened,
        onCloseMenuExamplesPopup,
        copyTableContent,
    } = useTopTrends();

    useEffect(() => {
        setIsShown(true);
    }, []);

    const menuExamplesMainFilter: MainFilter = {
        filters: backendFilters,
        includeLessOnePercentPenetration: includeLowPenetrationItems,
        includeLessTenMenusCount: includeLessTenMenusCount,
        isNew: showOnlyNewItems,
        yearsGrowth: growthPeriod,
        ...(drillWords.length > 0 ? { drillWords } : {}),
    };

    const {
        fetch: fetchMenuExamples,
        fetchExport: fetchMenuExamplesExport,
        downloadUrl: menuExamplesDownloadUrl,
    } = useTopTrendsMenuExamples({
        word: menuExamplesWordData,
        mainFilter: menuExamplesMainFilter,
        fullFilter: backendFilters,
    });

    const handleCopyTable = useCallback(() => {
        copyToClipboard(copyTableContent);

        dispatch(addNotification({
            text: copyTableContent ? "Successfully copied to your clipboard" : "Something went wrong",
            duration: 2000,
            error: !copyTableContent
        }));
    }, [copyTableContent, dispatch]);

    return (
        <div className="TopTrends">
            <CSSTransition in={isShown} classNames="appears1" timeout={3000} unmountOnExit>
                <div className="TopTrends__table-info-row">
                    <TableInfo
                        className="TopTrends__table-info"
                        drillWords={drillWords}
                        totalRecords={totalRecords}
                        handleDeleteItem={onDeleteDrillWord}
                    />
                </div>
            </CSSTransition>

            <CSSTransition in={isShown} classNames="appears1" timeout={3000} unmountOnExit>
                <TopTrendsControls/>
            </CSSTransition>

            <CSSTransition in={isShown} classNames="appears2" timeout={3000} unmountOnExit>
                <div className="TopTrends__content">
                    <TopTrendsTable/>

                    <div className="TopTrendsTable__copy-table-btn">
                        <ActionIcon
                            tooltip="copy table"
                            onClick={handleCopyTable}
                        >
                            <IconCopySimple/>
                        </ActionIcon>
                    </div>
                </div>
            </CSSTransition>

            <div className="TopTrends__footer">
                <FooterContent/>
            </div>

            <KeywordSettings
                shown={keywordSettingsState.isSettingsShown}
                x={keywordSettingsState.settingsX}
                y={keywordSettingsState.settingsY}
                searchingKeyword={keywordSettingsState.keyword?.name ?? ''}
                readOnly={true}
                closeModal={onCloseKeywordSettings}
                includes={(keywordSettingsState.keyword?.wordExtensions ?? []).filter(
                    item => item.wordType === 1 || item.wordType === 3
                )}
                excludes={(keywordSettingsState.keyword?.wordExtensions ?? []).filter(
                    item => item.wordType === 2
                )}
                keywords={[]}
                id={keywordSettingsState.keyword?.id ?? 0}
            />

            <TopTrendsMenuExamplePopup
                isShown={isMenuExamplesPopupOpened}
                close={onCloseMenuExamplesPopup}
                fetch={fetchMenuExamples}
                fetchExport={fetchMenuExamplesExport}
                downloadUrl={menuExamplesDownloadUrl}
            />
        </div>
    );
};

const TopTrends: FC = () => {
    const { isUserInfoLoaded } = usePlatformContext();

    if (!isUserInfoLoaded) return null;

    return (
        <Page title="Top Trends">
            <TopTrendsProvider>
                <TopTrendsContent/>
            </TopTrendsProvider>
        </Page>
    );
};

export default TopTrends;
